<template>
  <g
    class="circle-annotation"
    :class="{ selected, editing }"
  >
    <!-- Cercle principal -->
    <circle
      :cx="centerX"
      :cy="centerY"
      :r="radius"
      :stroke="selected ? '#4a9eff' : (annotation.color || '#00ff00')"
      :stroke-width="mainStrokeWidth"
      fill="none"
      class="circle-main"
      :class="{ draggable: selected }"
      @click.stop="handleClick"
      @mousedown.stop="selected ? handleStartCircleDrag($event) : null"
    />

    <!-- Croix centrale (toujours visible) -->
    <g>
      <line
        :x1="centerX - crossHalfSize"
        :y1="centerY"
        :x2="centerX + crossHalfSize"
        :y2="centerY"
        :stroke="selected ? '#4a9eff' : (annotation.color || '#00ff00')"
        :stroke-width="mainStrokeWidth"
        stroke-linecap="round"
        :pointer-events="selected ? 'stroke' : 'none'"
        class="control-point center cross-arm"
        @mousedown.stop="handleStartDrag($event, 'center')"
      />
      <line
        :x1="centerX"
        :y1="centerY - crossHalfSize"
        :x2="centerX"
        :y2="centerY + crossHalfSize"
        :stroke="selected ? '#4a9eff' : (annotation.color || '#00ff00')"
        :stroke-width="mainStrokeWidth"
        stroke-linecap="round"
        :pointer-events="selected ? 'stroke' : 'none'"
        class="control-point center cross-arm"
        @mousedown.stop="handleStartDrag($event, 'center')"
      />
      <!-- Hitbox invisible pour faciliter le drag du centre -->
      <circle
        v-if="selected"
        :cx="centerX"
        :cy="centerY"
        :r="handleRadius"
        fill="transparent"
        class="control-point center-hitbox"
        @mousedown.stop="handleStartDrag($event, 'center')"
      />
    </g>
    <!-- Points de contrôle complémentaires (visibles si sélectionné) -->
    <g v-if="selected">
      <!-- Point de rayon (à droite du centre) -->
      <circle
        :cx="centerX + radius"
        :cy="centerY"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point radius"
        @mousedown.stop="handleStartDrag($event, 'radius')"
      />
      <!-- Ligne de rayon -->
      <line
        :x1="centerX"
        :y1="centerY"
        :x2="centerX + radius"
        :y2="centerY"
        stroke="#4a9eff"
        :stroke-width="guideStrokeW"
        stroke-dasharray="8 4"
        pointer-events="none"
      />
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Annotation } from '@api/annotations.api';
import {
  ANNOTATION_DEFAULT_STROKE_PX,
  ANNOTATION_HANDLE_RADIUS_PX,
  ANNOTATION_HANDLE_STROKE_PX,
  ANNOTATION_CIRCLE_GUIDE_STROKE_PX,
} from '@/constants/annotations';

const props = defineProps<{
  annotation: Annotation;
  selected: boolean;
  svgRef?: SVGSVGElement | null;
  canvasPixelsPerUser?: number;
}>();

const emit = defineEmits<{
  select: [annotation: Annotation];
  update: [annotation: Annotation, data: any, save?: boolean];
  delete: [annotation: Annotation];
}>();

const editing = ref(false);
const dragging = ref(false);
const dragPoint = ref<'center' | 'radius' | 'circle' | null>(null);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartCircle = ref({ cx: 0, cy: 0, radius: 0 });
const lastDraggedData = ref<any | null>(null);

const centerX = computed(() => props.annotation.data.cx);
const centerY = computed(() => props.annotation.data.cy);
const radius = computed(() => props.annotation.data.radius);
const pixelsPerUser = computed(() => Math.max(props.canvasPixelsPerUser ?? 1, 0.01));
const strokeWidth = computed(() => props.annotation.stroke_width ?? ANNOTATION_DEFAULT_STROKE_PX);
const mainStrokeWidth = computed(() => (props.selected ? (strokeWidth.value + 1) : strokeWidth.value));
const handleRadius = computed(() => ANNOTATION_HANDLE_RADIUS_PX / pixelsPerUser.value);
const handleStroke = computed(() => ANNOTATION_HANDLE_STROKE_PX / pixelsPerUser.value);
const guideStrokeW = computed(() => ANNOTATION_CIRCLE_GUIDE_STROKE_PX / pixelsPerUser.value);
const crossHalfSize = computed(() => handleRadius.value * 0.75);

const handleClick = () => {
  if (!props.selected) {
    emit('select', props.annotation);
  }
};

const handleStartDrag = (event: MouseEvent, point: 'center' | 'radius') => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = point;
  lastDraggedData.value = null;
  event.preventDefault();
  event.stopPropagation();
};

const handleStartCircleDrag = (event: MouseEvent) => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = 'circle';
  lastDraggedData.value = null;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  dragStartPos.value = { x: svgP.x, y: svgP.y };
  dragStartCircle.value = {
    cx: props.annotation.data.cx,
    cy: props.annotation.data.cy,
    radius: props.annotation.data.radius,
  };
  
  event.preventDefault();
  event.stopPropagation();
};

const handleDragMove = (event: MouseEvent) => {
  if (!dragging.value || !dragPoint.value || !props.svgRef) return;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  const newData = { ...props.annotation.data };
  
  if (dragPoint.value === 'circle') {
    // Déplacer tout le cercle
    const deltaX = svgP.x - dragStartPos.value.x;
    const deltaY = svgP.y - dragStartPos.value.y;
    
    newData.cx = dragStartCircle.value.cx + deltaX;
    newData.cy = dragStartCircle.value.cy + deltaY;
  } else if (dragPoint.value === 'center') {
    // Déplacer le centre
    newData.cx = svgP.x;
    newData.cy = svgP.y;
  } else if (dragPoint.value === 'radius') {
    // Modifier le rayon
    const dx = svgP.x - newData.cx;
    const dy = svgP.y - newData.cy;
    newData.radius = Math.sqrt(dx * dx + dy * dy);
  }
  
  lastDraggedData.value = newData;
  emit('update', props.annotation, newData, false);
};

const handleDragEnd = () => {
  if (dragging.value) {
    const finalData = lastDraggedData.value;
    dragging.value = false;
    dragPoint.value = null;
    lastDraggedData.value = null;
    if (finalData) {
      emit('update', props.annotation, finalData, true);
    }
  }
};

onMounted(() => {
  window.addEventListener('mousemove', handleDragMove);
  window.addEventListener('mouseup', handleDragEnd);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove);
  window.removeEventListener('mouseup', handleDragEnd);
});
</script>

<style scoped>
.circle-annotation {
  cursor: pointer;
}

.circle-main {
  transition: stroke 0.2s, stroke-width 0.2s;
}

.circle-main:hover {
  opacity: 0.92;
}

.circle-main.draggable {
  cursor: move;
}

.control-point.center {
  cursor: move;
}

.control-point.center-hitbox {
  cursor: move;
}

.control-point.radius {
  cursor: ew-resize;
}
</style>
